import {ActivityIndicator, Image, Pressable, View} from 'react-native'
import {type AppBskyFeedDefs} from '@atproto/api'
import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react'
import {Trans} from '@lingui/react/macro'

import {DraggableScrollView} from '#/view/com/pager/DraggableScrollView'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import {Text} from '#/components/Typography'
import {useSemanticTokens} from '#/wsocial/service/design-system'
import {useTrendLabels} from '../data/labels'
import {type Trend, type TrendAvatar} from '../data/trends'
import {useTrendPosts} from '../data/useTrendPosts'
import {
  getVoicesDisplay,
  useImageLoadFailed,
  useTrendSearchPress,
} from '../util'
import {CompactPostPreview} from './CompactPostPreview'
import {TrendComposerPill} from './TrendComposerPrompt'
import {TrendRankHeader} from './TrendRankPill'

/**
 * Featured prominence trend card.
 *
 * Mirrors Figma node `384:53758` in file `C8J3bA4ThNVCG0vzlHeI16`. Used when
 * `section.cardStyle === 'featured'`. Sits between hero and compact in visual
 * weight.
 *
 * Currently renders: category · active voices, title, posts carousel
 * placeholder (until `GET /trends/{id}/posts` is wired), optional network
 * voices row (only when the trend carries `followedActiveCount` /
 * `followedActiveAvatars`, i.e. under `scope=foryou`), and the "Explore
 * trend" CTA.
 */
export function FeaturedTrendCard({
  trend,
  rankLabel,
}: {
  trend: Trend
  /** When set (Top trends #2), shows the rank pill + category header, a hero
   *  image, and a bottom active-voices row. */
  rankLabel?: string
}) {
  const heroImage = useImageLoadFailed(trend.heroImage?.url)
  const t = useTheme()
  const ss = useSemanticTokens()
  const {_} = useLingui()
  const goToTrendSearch = useTrendSearchPress()
  const {getCategoryLabel} = useTrendLabels()

  const networkCount = trend.followedActiveCount ?? 0
  const networkAvatars = trend.followedActiveAvatars ?? []
  const showNetworkRow = networkCount > 0 && networkAvatars.length > 0
  const voices = getVoicesDisplay(trend.metrics.activeVoicesCount)
  const rankAvatars = trend.participantAvatars.slice(0, 5)
  // Posts drive the bottom: carousel + "Explore trend" when present, otherwise
  // the "Start the discussion" composer (no empty box, no Explore button).
  const {posts, isLoading: postsLoading} = useTrendPosts(trend, {
    limit: 5,
    sort: 'top',
  })

  return (
    <View style={[a.w_full, a.gap_xl, a.py_2xl]}>
      {/* Heading block (category · active voices, title) is the trend press
          target. The posts carousel and Explore button below are separate. */}
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={_(msg`Explore trend: ${trend.title}`)}
        accessibilityHint={_(msg`Searches for this trend`)}
        onPress={() => goToTrendSearch(trend)}
        style={[a.px_lg, a.gap_md]}>
        {rankLabel ? (
          <TrendRankHeader
            rankLabel={rankLabel}
            categoryLabel={getCategoryLabel(trend.category)}
          />
        ) : (
          <View style={[a.flex_row, a.align_center, a.gap_xs]}>
            <Text
              style={[a.text_xs, a.font_bold, t.atoms.text_contrast_medium]}>
              {getCategoryLabel(trend.category)}
            </Text>
            {voices.showVoices && (
              <>
                <View
                  style={[
                    a.rounded_full,
                    {
                      width: 4,
                      height: 4,
                      backgroundColor: t.atoms.text_contrast_medium.color,
                      opacity: 0.97,
                    },
                  ]}
                />
                <Text
                  style={[
                    a.text_xs,
                    a.font_medium,
                    t.atoms.text_contrast_medium,
                  ]}>
                  {voices.showCount &&
                    `${formatCount(trend.metrics.activeVoicesCount ?? 0)} `}
                  {_(msg`active voices`)}
                </Text>
              </>
            )}
          </View>
        )}

        <Text style={[a.text_2xl, a.font_bold, t.atoms.text]}>
          {trend.title}
        </Text>

        {/* Top trends #2 carries a hero image between title and posts — shown
            only when present, no placeholder box otherwise. */}
        {rankLabel && trend.heroImage && !heroImage.failed && (
          <Image
            accessibilityIgnoresInvertColors
            source={{uri: trend.heroImage.url}}
            onError={heroImage.onError}
            style={[
              a.w_full,
              a.rounded_md,
              {aspectRatio: 176 / 92, backgroundColor: ss.bg.contrast_25},
            ]}
            resizeMode="cover"
          />
        )}
      </Pressable>

      {postsLoading ? (
        <FeaturedPostsPlaceholder />
      ) : posts.length > 0 ? (
        <FeaturedPostsCarousel posts={posts} />
      ) : (
        // No posts yet → invite the user to start the discussion in place of the
        // empty-state box (and the "Explore trend" button below is hidden too).
        <View style={[a.px_lg]}>
          <TrendComposerPill
            trend={trend}
            headingText={<Trans>Start the discussion</Trans>}
          />
        </View>
      )}

      {/* Top trends #2 shows an active-voices row under the posts. */}
      {rankLabel && voices.showVoices && rankAvatars.length > 0 && (
        <View style={[a.px_lg, a.flex_row, a.align_center, a.gap_sm]}>
          <NetworkAvatarStack avatars={rankAvatars} />
          <Text style={[a.text_xs, t.atoms.text_contrast_medium]}>
            {voices.showCount &&
              `${formatCount(trend.metrics.activeVoicesCount ?? 0)} `}
            {_(msg`active voices`)}
          </Text>
        </View>
      )}

      {/* Network voices row — only renders when foryou-scope data is present. */}
      {showNetworkRow && (
        <View style={[a.px_lg, a.flex_row, a.align_center, a.gap_sm]}>
          <NetworkAvatarStack avatars={networkAvatars.slice(0, 5)} />
          <Text style={[a.text_xs, t.atoms.text_contrast_medium]}>
            <Text style={[a.font_bold]}>{networkCount}</Text>{' '}
            <Trans>voices in your network</Trans>
          </Text>
        </View>
      )}

      {/* "Explore trend" only when there are posts; the no-posts state shows the
          composer pill above instead. */}
      {posts.length > 0 && (
        <View style={[a.px_lg]}>
          <Button
            label={_(msg`Explore trend: ${trend.title}`)}
            color="secondary_inverted"
            size="large"
            variant="solid"
            shape="default"
            onPress={() => goToTrendSearch(trend)}>
            <ButtonText>
              <Trans>Explore trend</Trans>
            </ButtonText>
          </Button>
        </View>
      )}
    </View>
  )
}

function FeaturedPostsPlaceholder() {
  return (
    <View
      style={[
        a.align_center,
        a.justify_center,
        {minHeight: 120, paddingLeft: 16},
      ]}>
      <ActivityIndicator />
    </View>
  )
}

function FeaturedPostsCarousel({posts}: {posts: AppBskyFeedDefs.PostView[]}) {
  return (
    <DraggableScrollView
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={[
        a.flex_row,
        // Default cross-axis stretch so every card matches the tallest one's
        // height; the preview fills that height via flex_1.
        {alignItems: 'stretch', paddingLeft: 16, paddingRight: 16, gap: 8},
      ]}>
      {posts.map(post => (
        <View key={post.uri} style={[{width: 350}]}>
          <CompactPostPreview post={post} style={a.flex_1} />
        </View>
      ))}
    </DraggableScrollView>
  )
}

function NetworkAvatarStack({avatars}: {avatars: TrendAvatar[]}) {
  const ss = useSemanticTokens()
  return (
    <View style={[a.flex_row, a.align_center]}>
      {avatars.map((avatar, i) => (
        <Image
          key={avatar.did}
          accessibilityIgnoresInvertColors
          source={{uri: avatar.avatarUrl}}
          style={[
            a.rounded_full,
            {
              width: 24,
              height: 24,
              marginLeft: i === 0 ? 0 : -8,
              borderWidth: 1,
              borderColor: 'rgba(0,0,0,0.2)',
              backgroundColor: ss.bg.contrast_25,
            },
          ]}
        />
      ))}
    </View>
  )
}

/* ---------- formatting + labels ---------- */

function formatCount(n: number): string {
  // Non-breaking-style thousands separator matches the Figma's number
  // formatting ("8 946").
  return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
}
